import type { Atom, WritableAtom } from 'jotai';
import { atom, useAtomValue, useSetAtom } from 'jotai';

import { ContractStepKeyT, STEP_KEYS } from '../config';
import {
  contractStepAtom,
  firstFundsErrorAnchorAtom,
  isAuthAgreementValidAtom,
  isFundsStepValidAtom,
  revealAuthAgreementErrorAtom,
  revealFundsErrorsAtom,
} from './atoms';

export type StepValidationT = {
  // True when every field this step owns is valid.
  isValidAtom: Atom<boolean>;
  // Write-only; flips this step's touched flags so its fields render their errors.
  revealErrorsAtom: WritableAtom<null, [], void>;
  // DOM id of the first invalid field in page order; null while the step is valid.
  // Omit for steps with no anchor.
  firstErrorAnchorAtom?: Atom<string | null>;
};

export type StepValidationResultT = {
  firstErrorAnchor: string | null;
  isValid: boolean;
  revealErrors: () => void;
};

// Default fallback for steps that have no fields to validate, and for when :step matches no step in the flow.
const alwaysValidAtom = atom(true);
const noAnchorAtom = atom<string | null>(null);
const noopRevealAtom = atom(null, () => {
  // A step with nothing to validate has no errors to reveal.
});

const ALWAYS_VALID: StepValidationT = {
  firstErrorAnchorAtom: noAnchorAtom,
  isValidAtom: alwaysValidAtom,
  revealErrorsAtom: noopRevealAtom,
};

/**
 * Declares what makes each step advanceable. ContractStep looks itself up here, so a page is
 * only its content — nothing about validation is repeated per page.
 *
 * This lives in the store rather than in config/contractSteps.ts on purpose: atoms.ts already
 * imports getContractSteps from ../config, so putting atoms in the config would make that cycle.
 */
export const STEP_VALIDATION: Record<ContractStepKeyT, StepValidationT> = {
  [STEP_KEYS.AUTH]: {
    isValidAtom: isAuthAgreementValidAtom,
    revealErrorsAtom: revealAuthAgreementErrorAtom,
  },
  [STEP_KEYS.FUNDS]: {
    firstErrorAnchorAtom: firstFundsErrorAnchorAtom,
    isValidAtom: isFundsStepValidAtom,
    revealErrorsAtom: revealFundsErrorsAtom,
  },
};

export const areAllStepsValidAtom = atom<boolean>((get) => get(contractStepAtom).every(
  ({ key }) => get(STEP_VALIDATION[key].isValidAtom),
));

/**
 * Every entry holds the same atoms, so this calls the same number of hooks for every step — the
 * step key can change without breaking hook order.
 */
export const useStepValidation = (stepKey?: ContractStepKeyT): StepValidationResultT => {
  const {
    firstErrorAnchorAtom,
    isValidAtom,
    revealErrorsAtom,
  } = (stepKey && STEP_VALIDATION[stepKey]) ?? ALWAYS_VALID;
  const firstErrorAnchor = useAtomValue(firstErrorAnchorAtom ?? noAnchorAtom);
  const isValid = useAtomValue(isValidAtom);
  const revealErrors = useSetAtom(revealErrorsAtom);

  return { firstErrorAnchor, isValid, revealErrors };
};

export const useRevealStepErrors = (stepKey?: ContractStepKeyT): (() => void) => {
  const { revealErrorsAtom } = (stepKey && STEP_VALIDATION[stepKey]) ?? ALWAYS_VALID;

  return useSetAtom(revealErrorsAtom);
};
